import { useContext, useState } from 'react';

import { Calendar as ReactBigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import {
    Ban,
    CalendarCheck,
    ExternalLink,
    PackageCheck,
    Plus,
    RefreshCw,
    Send,
    ShoppingCart,
    Sparkles,
    X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeContext } from 'styled-components';
import { format, getDay, getISOWeek, parse, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

import 'react-big-calendar/lib/css/react-big-calendar.css';

// Cast pour compatibilité @types/react-big-calendar vs React 18 (refs legacy)
const Calendar = ReactBigCalendar as unknown as React.JSX.ElementType;

import {
    GetSupplyOrdersQuery,
    SupplyOrderState,
    useGetSupplyOrdersQuery,
    useSupplyOrdersUpdatedOrCreatedSubscription,
} from 'data/__generated__';

import { ActionBar } from 'components/ActionBar';
import { Loader } from 'components/Loader';
import { MarkAsAbortedPopup } from 'pages/SupplyOrders/Popups/MarkAsAbortedPopup';
import { CreateSupplyOrderPopup } from './Popups/CreateSupplyOrderPopup';
import { GenerateSupplyOrdersNeededPopup } from './Popups/GenerateSupplyOrdersNeededPopup';
import { MarkAsConfirmedPopup } from './Popups/MarkAsConfirmedPopup';
import { MarkAsOrderedPopup } from './Popups/MarkAsOrderedPopup';
import { MarkAsReadyPopup } from './Popups/MarkAsReadyPopup';
import { MarkAsReceivedPopup } from './Popups/MarkAsReceivedPopup';
import { RelaunchPassSupplyOrdersToOrderedPopup } from 'pages/SupplyOrders/Popups/RelaunchPassSupplyOrdersToOrderedPopup';

import { supplyOrdersUpdatedOrCreatedHandler } from 'pages/SupplyOrders/cacheHandlers/supplyOrdersCacheHandler';
import { dateFromString, formatDateAsAnniversary } from '../../helpers/dateTimes';
import { getColorsFromState } from './constants/states';
import { SupplyOrderCalendarEvent } from './components/SupplyOrderCalendarEvent';

import { FreshnessFilter } from './index';

const locales = { fr };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

type SupplyOrderEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    supplyOrder: GetSupplyOrdersQuery['supplyOrders'][number];
    backgroundColor: string;
};

export const SupplyOrdersTimelineView = ({
    displayLimitDate,
    supplierIds = [],
    filterDelayed = false,
    freshnessFilter = 'all',
    warehouseColumnId = null,
    stateFilter = [],
}: {
    displayLimitDate: Date;
    supplierIds?: string[];
    filterDelayed?: boolean;
    freshnessFilter?: FreshnessFilter;
    warehouseColumnId?: string | null;
    stateFilter?: string[];
}) => {
    const now = new Date();
    const theme = useContext(ThemeContext);
    const navigate = useNavigate();
    const [selectedSupplyOrderIds, setSelectedSupplyOrderIds] = useState<string[]>([]);
    const [isCreateSupplyOrderPopupOpen, setIsCreateSupplyOrderPopupOpen] = useState<boolean>(false);
    const [isGenerateSupplyOrdersNeededOpen, setIsGenerateSupplyOrdersNeededOpen] = useState<boolean>(false);
    const [isRelaunchPassToOrderedPopupOpen, setIsRelaunchPassToOrderedPopupOpen] = useState<boolean>(false);
    const [isMarkAsConfirmedPopupOpen, setIsMarkAsConfirmedPopupOpen] = useState<boolean>(false);
    const [isMarkAsOrderedPopupOpen, setIsMarkAsOrderedPopupOpen] = useState<boolean>(false);
    const [isMarkAsReadyPopupOpen, setIsMarkAsReadyPopupOpen] = useState<boolean>(false);
    const [isMarkAsReceivedPopupOpen, setIsMarkAsReceivedPopupOpen] = useState<boolean>(false);
    const [isMarkAsAbortedPopupOpen, setIsMarkAsAbortedPopupOpen] = useState<boolean>(false);

    const {
        data: supplyOrdersData,
        loading: supplyOrdersLoading,
        error: supplyOrdersError,
    } = useGetSupplyOrdersQuery({
        fetchPolicy: 'network-only',
        variables: {
            displayLimitDateFormatted: formatDateAsAnniversary({ dateTime: displayLimitDate, useNewFormat: true }),
        },
    });

    useSupplyOrdersUpdatedOrCreatedSubscription({
        onData({ data: { data } }) {
            if (!data) {
                return;
            }

            const { supplyOrdersUpdatedOrCreated } = data;
            supplyOrdersUpdatedOrCreatedHandler(supplyOrdersUpdatedOrCreated);
            toast.warning("Quelqu'un vient de modifier un ou plusieurs achat(s), la vue a été mise à jour !");
        },
    });

    if (supplyOrdersLoading && !supplyOrdersData) {
        return (
            <Content>
                <Loader />
            </Content>
        );
    }

    if (supplyOrdersError || !supplyOrdersData) {
        throw new Error('Une erreur est survenue lors de la récupération des achats');
    }

    const supplyOrders = supplyOrdersData.supplyOrders.filter((supplyOrder) => {
        const matchesSupplier = supplierIds.length === 0 || supplierIds.includes(supplyOrder.supplier._id);
        const matchesDelayed =
            !filterDelayed ||
            (supplyOrder.dateDeliveryScheduled &&
                !supplyOrder.dateDelivery &&
                dateFromString(supplyOrder.dateDeliveryScheduled) < now);

        let matchesFreshness = true;
        if (freshnessFilter === 'fresh') {
            matchesFreshness = !!supplyOrder.weeklyMenusStartDate;
        } else if (freshnessFilter === 'sec') {
            matchesFreshness = !supplyOrder.weeklyMenusStartDate;
        }

        const matchesWarehouse = !warehouseColumnId || supplyOrder.warehouseColumn?._id === warehouseColumnId;
        const matchesState = stateFilter.length === 0 || stateFilter.includes(supplyOrder.state);

        return matchesSupplier && matchesDelayed && matchesFreshness && matchesWarehouse && matchesState;
    });

    function toggleSupplyOrderSelection(supplyOrderIdToToggle: string) {
        if (selectedSupplyOrderIds.includes(supplyOrderIdToToggle)) {
            setSelectedSupplyOrderIds(
                selectedSupplyOrderIds.filter((supplyOrderId) => supplyOrderId !== supplyOrderIdToToggle),
            );
        } else {
            setSelectedSupplyOrderIds([...selectedSupplyOrderIds, supplyOrderIdToToggle]);
        }
    }

    function mapSupplyOrderToEvent(supplyOrder: GetSupplyOrdersQuery['supplyOrders'][number]): SupplyOrderEvent | null {
        const dateString = supplyOrder.dateDeliveryScheduled || supplyOrder.orderDate;
        if (!dateString) {
            return null;
        }

        const isNewFormat = !supplyOrder.dateDeliveryScheduled && !!supplyOrder.orderDate;
        const date = dateFromString(dateString, isNewFormat);

        const { backgroundColor } = getColorsFromState(
            supplyOrder.state,
            supplyOrder.weeklyMenusStartDate,
            supplyOrder.isMerged,
        );

        return {
            id: supplyOrder._id,
            title: `${supplyOrder.supplier.name} #${supplyOrder.number || '?'}`,
            start: date,
            end: date,
            allDay: true,
            supplyOrder,
            backgroundColor,
        };
    }

    const events: SupplyOrderEvent[] = supplyOrders
        .map(mapSupplyOrderToEvent)
        .filter((event): event is SupplyOrderEvent => event !== null);

    const components = {
        event: ({ event }: { event: SupplyOrderEvent }) => (
            <SupplyOrderCalendarEvent
                supplyOrder={event.supplyOrder}
                isSelected={selectedSupplyOrderIds.includes(event.id)}
            />
        ),
    };

    function handleSelectEvent(event: SupplyOrderEvent) {
        toggleSupplyOrderSelection(event.id);
    }

    function handleDoubleClickEvent(event: SupplyOrderEvent) {
        navigate(`/supply-order/${event.id}`);
    }

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <Plus />,
                    label: 'Créer',
                    onClick: () => {
                        setIsCreateSupplyOrderPopupOpen(true);
                    },
                    hidden: false,
                },
                {
                    icon: <Sparkles />,
                    label: 'Générer les achats nécessaires',
                    onClick: () => {
                        setIsGenerateSupplyOrdersNeededOpen(true);
                    },
                    hidden: false,
                },
                {
                    icon: <RefreshCw />,
                    label: 'Relancer le passage auto en "Commandé"',
                    onClick: () => {
                        setIsRelaunchPassToOrderedPopupOpen(true);
                    },
                    hidden: false,
                },
                {
                    icon: <ExternalLink />,
                    label: 'Ouvrir',
                    onClick: () => {
                        navigate(`/supply-order/${selectedSupplyOrderIds[0]}`);
                    },
                    hidden: selectedSupplyOrderIds.length !== 1,
                },
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => {
                        setSelectedSupplyOrderIds([]);
                    },
                    hidden: !selectedSupplyOrderIds.length,
                },
            ],
        },
        {
            title: 'Marquer comme',
            actions: [
                {
                    icon: <Send />,
                    label: 'à envoyer',
                    onClick: () => {
                        setIsMarkAsReadyPopupOpen(true);
                    },
                    hidden:
                        !selectedSupplyOrderIds.length ||
                        supplyOrders.some(
                            ({ _id, state }) =>
                                selectedSupplyOrderIds.includes(_id) && state !== SupplyOrderState.Created,
                        ),
                },
                {
                    icon: <ShoppingCart />,
                    label: 'commandée(s)',
                    onClick: () => {
                        setIsMarkAsOrderedPopupOpen(true);
                    },
                    hidden:
                        !selectedSupplyOrderIds.length ||
                        supplyOrders.some(
                            ({ _id, state }) =>
                                selectedSupplyOrderIds.includes(_id) && state !== SupplyOrderState.Ready,
                        ),
                },
                {
                    icon: <CalendarCheck />,
                    label: 'confirmée(s)',
                    onClick: () => {
                        setIsMarkAsConfirmedPopupOpen(true);
                    },
                    hidden:
                        !selectedSupplyOrderIds.length ||
                        supplyOrders.some(
                            ({ _id, state }) =>
                                selectedSupplyOrderIds.includes(_id) && state !== SupplyOrderState.Ordered,
                        ),
                },
                {
                    icon: <PackageCheck />,
                    label: 'reçues(s)',
                    onClick: () => {
                        setIsMarkAsReceivedPopupOpen(true);
                    },
                    hidden:
                        !selectedSupplyOrderIds.length ||
                        supplyOrders.some(
                            ({ _id, state }) =>
                                selectedSupplyOrderIds.includes(_id) &&
                                ![SupplyOrderState.Ordered, SupplyOrderState.Confirmed].includes(state),
                        ),
                },
                {
                    icon: <Ban />,
                    label: 'annulée(s)',
                    onClick: () => {
                        setIsMarkAsAbortedPopupOpen(true);
                    },
                    hidden:
                        !selectedSupplyOrderIds.length ||
                        supplyOrders.some(
                            ({ _id, state }) =>
                                selectedSupplyOrderIds.includes(_id) &&
                                ![
                                    SupplyOrderState.Created,
                                    SupplyOrderState.Ready,
                                    SupplyOrderState.Ordered,
                                    SupplyOrderState.Confirmed,
                                    SupplyOrderState.OnHold,
                                    SupplyOrderState.Aborted,
                                ].includes(state),
                        ),
                },
            ],
        },
    ];

    return (
        <Content>
            <CalendarContainer>
                <Calendar
                    localizer={localizer}
                    events={events}
                    eventPropGetter={(event: SupplyOrderEvent) => ({
                        style: { backgroundColor: 'transparent' },
                    })}
                    dayPropGetter={(date: Date) => {
                        const today = new Date();
                        const isToday =
                            date.getDate() === today.getDate() &&
                            date.getMonth() === today.getMonth() &&
                            date.getFullYear() === today.getFullYear();
                        return {
                            style: {
                                backgroundColor: isToday ? theme?.backgroundColor : theme?.cardBackgroundColor,
                            },
                        };
                    }}
                    startAccessor="start"
                    endAccessor="end"
                    views={['month', 'week']}
                    defaultView="week"
                    formats={{
                        dayFormat: (date: Date) => format(date, 'EEEE dd MMM', { locale: fr }),
                        dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
                            `S${getISOWeek(start)} ${format(start, 'dd MMM', { locale: fr })} – ${format(end, 'dd MMM', { locale: fr })}`,
                    }}
                    components={components}
                    onSelectEvent={handleSelectEvent}
                    onDoubleClickEvent={handleDoubleClickEvent}
                    style={{ height: '100%', width: '100%' }}
                    culture="fr"
                    messages={{
                        previous: 'Précédent',
                        next: 'Suivant',
                        today: "Aujourd'hui",
                        showMore: (total: number) => `+${total} de plus`,
                        week: 'Semaine',
                        month: 'Mois',
                    }}
                />
            </CalendarContainer>
            <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedSupplyOrderIds.length} />
            <CreateSupplyOrderPopup isOpen={isCreateSupplyOrderPopupOpen} setIsOpen={setIsCreateSupplyOrderPopupOpen} />
            <GenerateSupplyOrdersNeededPopup
                isOpen={isGenerateSupplyOrdersNeededOpen}
                setIsOpen={setIsGenerateSupplyOrdersNeededOpen}
            />
            <RelaunchPassSupplyOrdersToOrderedPopup
                isOpen={isRelaunchPassToOrderedPopupOpen}
                setIsOpen={setIsRelaunchPassToOrderedPopupOpen}
            />
            <MarkAsConfirmedPopup
                supplyOrders={supplyOrders}
                selectedSupplyOrderIds={selectedSupplyOrderIds}
                setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                isOpen={isMarkAsConfirmedPopupOpen}
                setIsOpen={setIsMarkAsConfirmedPopupOpen}
            />
            <MarkAsOrderedPopup
                supplyOrders={supplyOrders}
                selectedSupplyOrderIds={selectedSupplyOrderIds}
                setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                isOpen={isMarkAsOrderedPopupOpen}
                setIsOpen={setIsMarkAsOrderedPopupOpen}
            />
            <MarkAsReadyPopup
                supplyOrders={supplyOrders}
                selectedSupplyOrderIds={selectedSupplyOrderIds}
                setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                isOpen={isMarkAsReadyPopupOpen}
                setIsOpen={setIsMarkAsReadyPopupOpen}
            />
            <MarkAsReceivedPopup
                supplyOrders={supplyOrders}
                selectedSupplyOrderIds={selectedSupplyOrderIds}
                setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                isOpen={isMarkAsReceivedPopupOpen}
                setIsOpen={setIsMarkAsReceivedPopupOpen}
            />
            <MarkAsAbortedPopup
                supplyOrders={supplyOrders}
                selectedSupplyOrderIds={selectedSupplyOrderIds}
                setSelectedSupplyOrderIds={setSelectedSupplyOrderIds}
                isOpen={isMarkAsAbortedPopupOpen}
                setIsOpen={setIsMarkAsAbortedPopupOpen}
            />
        </Content>
    );
};

const CalendarContainer = styled.div`
    flex: 1;
    overflow: auto;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 10px;

    .rbc-event {
        padding: 0;
        margin: 1px 0;
        background-color: transparent;
        border: none;
    }

    .rbc-event:focus {
        outline: none;
    }

    .rbc-show-more {
        font-size: 12px;
        font-weight: 600;
    }

    .rbc-time-view {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .rbc-time-content {
        display: none;
    }

    .rbc-time-header {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-time-header-content {
        border: none;
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-allday-cell {
        flex: 1;
        overflow-y: auto;
    }

    .rbc-row-content {
        min-height: 100%;
    }

    .rbc-toolbar {
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-toolbar button:hover,
    .rbc-toolbar button:focus {
        background-color: ${({ theme }) => theme.backgroundColor};
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button.rbc-active {
        background-color: ${({ theme }) => theme.ctaPrimaryColor};
        color: ${({ theme }) => theme.ctaPrimaryTextColor};
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }

    .rbc-header {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
        padding: 8px 4px;
        font-weight: 600;
    }

    .rbc-month-view,
    .rbc-time-view {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-day-bg + .rbc-day-bg,
    .rbc-month-row + .rbc-month-row {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-off-range-bg {
        background-color: ${({ theme }) => theme.backgroundColor};
    }

    .rbc-date-cell {
        color: ${({ theme }) => theme.textColor};
        padding: 4px 8px;
    }

    .rbc-off-range {
        color: ${({ theme }) => theme.disabledTextColor || theme.lightBorderColor};
    }
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;
